import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { Project } from 'src/app/interfaces/api/project';
import { PostTracker, PutTracker, Tracker } from 'src/app/interfaces/api/tracker';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from '../../services/api/trackers/tracker.service';

@Injectable({
    providedIn: 'root'
})

export class TrackerTool {
    configs: TrackerConfig[] = [];

    constructor(
        private trackerService: TrackerService,
        private translateService: TranslateService
    ) { }

    setConfigs(configs: TrackerConfig[]) {
        this.configs = configs;
    }


    getConfigs(tracker: Tracker): Promise<TrackerConfig[]> {
        return new Promise(resolve => {
            if (this.configs.length) {
                this.setDefaultConfig(tracker);
                return resolve(this.configs);
            }
            this.trackerService.getConfigs().subscribe((res: any[]) => {
                this.configs = res;
                this.setDefaultConfig(tracker);
                resolve(this.configs);
            });
        });
    }

    setDefaultConfig(tracker: Tracker) {
        if (!tracker.config) {
            let defaultConfig = this.configs.find(c => c.name == "Default Unit");
            switch (tracker.type) {
                case 'GPS RTK':
                    defaultConfig = this.configs.find(c => c.name == "Default GPS RTK");
                    break;
                case 'ELSA':
                    defaultConfig = this.configs.find(c => c.name == "Default ELSA");
                    break;
                case 'KYD':
                    defaultConfig = this.configs.find(c => c.name == "Default KYD");
                    break;
                case 'Tablet':
                    defaultConfig = this.configs.find(c => c.name == "Default Tablet");
                    break;
                default:
                    break;
            }

            if (defaultConfig) {
                tracker.config = defaultConfig;
                tracker.type = defaultConfig.type;
            }
        }
        return tracker;
    }

    getStats(tracker: Tracker, destroy: Subject<void>, projectId?: string): Promise<Tracker> {
        return new Promise((resolve, reject) => {
            let stats: string = "";
            if (!projectId)
                projectId = typeof tracker.project == "string" ? tracker.project as string : (tracker.project)?.id;
            if (!projectId)
                return reject("Project id argument required if tracker.project missing");
            this.trackerService.getStats(tracker.name, projectId || tracker.project as string).pipe(takeUntil(destroy)).subscribe({
                next: res => {
                    if (res && res.stats) {
                        let count = 0;

                        if (res.projectName && projectId) {
                            if (!(tracker?.project as Project)?.id && projectId.toString() == (tracker.project as string).toString()) {
                                tracker.project = { id: projectId, name: res.projectName } as Project;
                            }
                        }

                        res.stats.forEach((s: any) => {
                            stats += `${format(s.createdDate, 'dd/MM/yyyy')}: ${s.count} \n`;
                            count += s.count;
                        })

                        if (count == 0) {
                            stats += this.translateService.instant("trackers.stats.no_data");
                        } else {
                            stats += this.translateService.instant("trackers.stats.total", { count });
                        }
                        tracker.stats = stats;
                    }
                    resolve(tracker);
                },
                error: err => {
                    if (/not found/i.test(err.error.error)) {
                        stats = this.translateService.instant("trackers.stats.no_tracker");
                    } else {
                        stats = this.translateService.instant("trackers.stats.error");
                    }
                    tracker.stats = stats;
                    resolve(tracker);
                }
            })
        })
    }

    resolveTrackerType(tracker: Tracker | PostTracker | PutTracker): void {
        const config = this.configs.find(c => c.id === tracker.config || c.id === (tracker.config as TrackerConfig)?.id);

        if (config) tracker.type = config.type;

    }

    isWithinOfflineDelay(tracker: any, configs?: any[]) {
        let unit: any = "seconds";
        let time = 1;

        if (tracker.config && tracker.config.offlineDelay) {
            time = tracker.config.offlineDelay;
        } else if (tracker.offlineDelay) {
            time = tracker.offlineDelay;
        } else if (configs && configs.length > 0 && tracker.type) {
            let defaultConfigs = configs && configs.filter(config => /Default/i.test(config.name));
            let type: string = tracker.type;
            const matchedConfig = defaultConfigs.find(config => config.name.includes(type));
            if (matchedConfig && matchedConfig.offlineDelay)
                time = matchedConfig.offlineDelay;
            else
                unit = "minutes";
        } else {
            unit = "minutes";
        }

        var age = moment().diff(tracker.lastUpdate, unit, true);
        
        if (age && age < time) {
            return true;
        } else {
            return false;
        }
    }
}